import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { printHTML } from '../utils/printHelper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash, Printer, FilePdf, MagnifyingGlass, IdentificationBadge, X as XIcon } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import FizickoLiceAutocomplete from '../components/FizickoLiceAutocomplete';
import ClientAutocomplete from '../components/ClientAutocomplete';

const EMPTY = {
  client_id: '',
  subj_naziv: '', subj_skraceni_naziv: '', subj_pib: '', subj_adresa: '', subj_telefon: '',
  odg_ime_prezime: '', odg_jmb: '',
  ovl_ime_prezime: '', ovl_jmb: '', ovl_opstina: '', ovl_adresa: '',
  tip_ovlascenja: 'punomocnik',
  stvarni_vlasnici: [],
  datum_izdavanja: new Date().toISOString().slice(0, 10),
  napomena: '',
};

function formatDMY(d) {
  if (!d) return '';
  const [y, m, dd] = d.split('-');
  return `${dd}.${m}.${y}`;
}

export default function RsvPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get('/rsv'), api.get('/clients')]);
      setItems(r.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return items;
    return items.filter(i =>
      (i.client_naziv || '').toLowerCase().includes(q) ||
      (i.ovl_ime_prezime || '').toLowerCase().includes(q) ||
      (i.subj_pib || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const pullFromClient = (client_id) => {
    const c = clients.find(x => x.id === client_id);
    if (!c) { setForm({ ...form, client_id }); return; }
    const full = c.naziv_firme || c.skraceni_naziv || '';
    const adresa = [c.adresa, c.grad].filter(Boolean).join(', ');
    setForm({
      ...form,
      client_id,
      subj_naziv: full.toUpperCase(),
      subj_skraceni_naziv: (c.skraceni_naziv || '').toUpperCase(),
      subj_pib: c.pib || '',
      subj_adresa: adresa.toUpperCase() || (c.grad || '').toUpperCase(),
      subj_telefon: c.telefon || '',
      odg_ime_prezime: (c.direktor || '').toUpperCase(),
      odg_jmb: c.jmb_direktora || '',
    });
  };

  const refreshFromClient = () => {
    if (!form.client_id) return toast.error('Prvo izaberite klijenta');
    pullFromClient(form.client_id);
    toast.success('Podaci povuceni iz kartice klijenta');
  };

  const addSV = () => setForm(f => ({ ...f, stvarni_vlasnici: [...(f.stvarni_vlasnici || []), { ime_prezime: '', jmb: '' }] }));
  const removeSV = (i) => setForm(f => ({ ...f, stvarni_vlasnici: f.stvarni_vlasnici.filter((_, idx) => idx !== i) }));
  const updateSV = (i, key, val) => setForm(f => ({
    ...f,
    stvarni_vlasnici: f.stvarni_vlasnici.map((sv, idx) => idx === i ? { ...sv, [key]: val } : sv),
  }));

  const handleSave = async () => {
    if (!form.client_id) return toast.error('Izaberite klijenta');
    if (!form.ovl_ime_prezime) return toast.error('Unesite ovlašćeno lice');
    if (!form.ovl_jmb) return toast.error('Unesite JMB ovlašćenog lica');
    try {
      if (editingId) {
        await api.put(`/rsv/${editingId}`, form);
        toast.success('RSV ažurirano');
      } else {
        await api.post('/rsv', form);
        toast.success('RSV kreirano');
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || x).join(', ') : (d || 'Greska');
      toast.error(msg);
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setForm({
      client_id: r.client_id,
      subj_naziv: r.subj_naziv || '',
      subj_skraceni_naziv: r.subj_skraceni_naziv || '',
      subj_pib: r.subj_pib || '',
      subj_adresa: r.subj_adresa || '',
      subj_telefon: r.subj_telefon || '',
      odg_ime_prezime: r.odg_ime_prezime || '',
      odg_jmb: r.odg_jmb || '',
      ovl_ime_prezime: r.ovl_ime_prezime || '',
      ovl_jmb: r.ovl_jmb || '',
      ovl_opstina: r.ovl_opstina || '',
      ovl_adresa: r.ovl_adresa || '',
      tip_ovlascenja: r.tip_ovlascenja || 'punomocnik',
      stvarni_vlasnici: r.stvarni_vlasnici || [],
      datum_izdavanja: r.datum_izdavanja || '',
      napomena: r.napomena || '',
    });
    setOpen(true);
  };

  const handleDelete = async (r) => {
    if (!await confirmDialog({ title: 'Brisanje RSV zapisa', message: `Obrisati RSV zapis za ${r.client_naziv}?`, confirmText: 'Obrisi', variant: 'danger' })) return;
    try {
      await api.delete(`/rsv/${r.id}`);
      toast.success('Obrisano');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska pri brisanju');
    }
  };

  // ========== OBRAZAC 1: OVLASCENJE (ZAKONSKI TEKST - DOSLOVAN) ==========
  const buildObrazac1 = (r) => {
    const tipCheck = (v) => (r.tip_ovlascenja === v) ? 'x' : '';
    const svLines = (r.stvarni_vlasnici && r.stvarni_vlasnici.length > 0)
      ? r.stvarni_vlasnici.map(sv => `${(sv.ime_prezime || '').toUpperCase()}, JMB: ${sv.jmb || '_____________'}`).join('; ')
      : '_________________________________';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Obrazac 1 — Ovlašćenje RSV</title>
<style>
@page { size: A4; margin: 22mm 24mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; color: #000; line-height: 1.5; }
.form-id { text-align: left; font-size: 11pt; margin-bottom: 10px; }
.title { text-align: center; font-weight: 700; font-size: 13pt; text-transform: uppercase; margin: 14px 0 26px 0; line-height: 1.4; }
.subj-block { font-size: 12pt; margin-bottom: 20px; line-height: 1.75; }
.subj-block .row { display: block; }
.subj-block strong { font-weight: 700; }
.para { text-align: justify; margin: 14px 0; font-size: 12pt; line-height: 1.75; }
.center-title { text-align: center; font-weight: 700; font-size: 13pt; margin: 22px 0 16px 0; }
.tip-line { font-size: 12pt; margin: 18px 0 8px 0; }
.tip-row { margin: 4px 0 10px 0; font-size: 12pt; }
.tip-row .chk { display: inline-block; width: 18px; height: 18px; border: 1.4px solid #000; text-align: center; font-weight: 700; margin: 0 6px 0 14px; line-height: 16px; vertical-align: middle; font-family: 'Times New Roman', Times, serif; }
.sig-tbl { width: 100%; border-collapse: collapse; margin-top: 55px; }
.sig-tbl td { vertical-align: top; padding: 0 4px; font-size: 12pt; }
.sig-tbl .date-col { width: 35%; }
.sig-tbl .odg-col { width: 65%; text-align: center; }
.sig-tbl .mp { font-weight: 700; font-size: 13pt; margin: 14px 0 6px 0; letter-spacing: 2px; }
.sig-tbl .sigline { border-top: 1px solid #000; margin-top: 40px; padding-top: 4px; font-weight: 600; display: inline-block; min-width: 240px; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="form-id">Obrazac 1</div>

<div class="title">OVLAŠĆENJE ZA UNOS, AŽURIRANJE,<br/>PROVJERU I POTVRDU PODATAKA U REGISTAR<br/>STVARNIH VLASNIKA</div>

<div class="subj-block">
  <span class="row"><strong>Naziv subjekta:</strong> ${(r.subj_skraceni_naziv || r.subj_naziv || '____________________').toUpperCase()}</span>
  <span class="row"><strong>PIB:</strong> ${r.subj_pib || '____________'}</span>
  <span class="row"><strong>Adresa:</strong> ${(r.subj_adresa || '____________________').toUpperCase()}</span>
  <span class="row"><strong>Telefon:</strong> ${r.subj_telefon || '____________________'}</span>
</div>

<p class="para">
  ${(r.subj_naziv || '____________________________').toUpperCase()}, kojeg zastupa odgovorno lice u subjektu, ${(r.odg_ime_prezime || '____________________').toUpperCase()}, JMB ${r.odg_jmb || '_____________'}, izdaje
</p>

<div class="center-title">OVLAŠĆENJE</div>

<p class="para">
  ${(r.ovl_ime_prezime || '____________________').toUpperCase()}, JMB ${r.ovl_jmb || '_____________'}${r.ovl_opstina ? `, opština ${r.ovl_opstina}` : ''}${r.ovl_adresa ? `, ${r.ovl_adresa}` : ''}, da u Registar stvarnih vlasnika, koji se vodi na osnovu člana 43 Zakona o sprečavanju pranja novca i finansiranja terorizma, za ${(r.subj_naziv || '______________________________').toUpperCase()}, unese/ažurira/provjeri/ potvrdi podatke o stvarnom vlasniku / stvarnim vlasnicima :${svLines}.
</p>

<p class="tip-line">Ovlašćenje se izdaje:</p>
<p class="tip-row">
  <span class="chk">${tipCheck('zaposleni')}</span> zaposlenom
  <span class="chk">${tipCheck('zastupnik')}</span> zastupniku
  <span class="chk">${tipCheck('punomocnik')}</span> punomoćniku
</p>

<table class="sig-tbl">
  <tr>
    <td class="date-col">
      <div><strong>Datum:</strong></div>
      <div style="margin-top: 6px;">${formatDMY(r.datum_izdavanja) || ''}</div>
    </td>
    <td class="odg-col">
      <div><strong>Odgovorno lice u subjektu:</strong></div>
      <div class="mp">M.P.</div>
      <div class="sigline">${(r.odg_ime_prezime || '').toUpperCase()}</div>
    </td>
  </tr>
</table>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  // ========== OBRAZAC 2: IZJAVA (ZAKONSKI TEKST - CENTRIRANO NA A4) ==========
  const buildObrazac2 = (r) => {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Obrazac 2 — Izjava RSV</title>
<style>
@page { size: A4; margin: 22mm 24mm; }
* { margin:0; padding:0; box-sizing:border-box; }
html, body { height: 100%; }
body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; color: #000; line-height: 1.55; position: relative; min-height: 253mm; }
.form-id { position: absolute; top: 0; left: 0; font-size: 11pt; }
.content-wrap { position: absolute; top: 50%; left: 0; right: 0; transform: translateY(-50%); padding: 0 4mm; }
.title { text-align: center; font-weight: 700; font-size: 13pt; text-transform: uppercase; margin: 0 0 36px 0; line-height: 1.4; }
.ja-line { text-align: center; font-size: 12pt; margin: 20px 0; line-height: 1.75; }
.center-word { text-align: center; font-weight: 700; font-size: 13pt; margin: 26px 0 22px 0; }
.para { text-align: justify; margin: 18px 0; font-size: 12pt; line-height: 1.75; }
.sig-tbl { width: 100%; border-collapse: collapse; margin-top: 70px; }
.sig-tbl td { vertical-align: top; padding: 0 4px; font-size: 12pt; }
.sig-tbl .date-col { width: 45%; }
.sig-tbl .davalac-col { width: 55%; text-align: center; }
.sig-tbl .sigline { border-top: 1px solid #000; margin-top: 40px; padding-top: 4px; font-weight: 600; display: inline-block; min-width: 240px; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="form-id">Obrazac 2</div>

<div class="content-wrap">
  <div class="title">IZJAVA OVLAŠĆENOG LICA ZA UNOS PODATAKA U<br/>REGISTAR STVARNIH VLASNIKA O PROVJERI I<br/>POTVRDI TAČNOSTI PODATAKA</div>

  <p class="ja-line">
    Ja ${(r.ovl_ime_prezime || '____________________').toUpperCase()}, JMB ${r.ovl_jmb || '_____________'},
  </p>

  <div class="center-word">Izjavljujem</div>

  <p class="para">
    Da su svi podaci o stvarnom vlasniku subjekta ${(r.subj_naziv || '______________________________').toUpperCase()}, PIB: ${r.subj_pib || '____________'} i drugi podaci koji su unijeti u Registar stvarnih vlasnika provjereni i tačni.
  </p>

  <table class="sig-tbl">
    <tr>
      <td class="date-col">
        <div><strong>Datum:</strong></div>
        <div style="margin-top: 6px;">${formatDMY(r.datum_izdavanja) || ''}</div>
      </td>
      <td class="davalac-col">
        <div><strong>Davalac izjave,</strong></div>
        <div class="sigline">${(r.ovl_ime_prezime || '').toUpperCase()}</div>
      </td>
    </tr>
  </table>
</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const print1 = (r) => printHTML(buildObrazac1(r));
  const print2 = (r) => printHTML(buildObrazac2(r));

  const exportPdf = async (r, which) => {
    const htmlStr = which === 1 ? buildObrazac1(r) : buildObrazac2(r);
    // Ukloni <script> blok (window.print) prije slanja - inace WeasyPrint baci na print pri generisanju
    const cleanHtml = htmlStr.replace(/<script[\s\S]*?<\/script>/gi, '');
    const filename = `RSV_Obrazac${which}_${(r.client_naziv || 'klijent').replace(/\s+/g, '_')}.pdf`;
    try {
      toast.loading('Generišem PDF...', { id: `pdf-${r.id}-${which}` });
      const res = await api.post('/rsv/export-pdf', { html: cleanHtml, filename }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('PDF preuzet', { id: `pdf-${r.id}-${which}` });
    } catch (e) {
      // Backend salje JSON detail ali responseType je blob — moramo ga pretvoriti u tekst.
      // Pokušajmo na više načina jer Cloudflare ponekad presretne i vrati HTML/string.
      let detail = 'Greska pri generisanju PDF-a';
      try {
        const blob = e.response?.data;
        if (blob) {
          if (typeof blob.text === 'function') {
            const txt = await blob.text();
            try {
              const parsed = JSON.parse(txt);
              detail = parsed.detail || txt.slice(0, 300);
            } catch {
              // Nije JSON — vjerovatno Cloudflare HTML stranica; uzmi prvih 300 znakova
              detail = txt.slice(0, 300) || detail;
            }
          } else if (typeof blob === 'string') {
            detail = blob.slice(0, 300);
          }
        } else if (e.message) {
          detail = `${e.message} (status ${e.response?.status || 'unknown'})`;
        }
      } catch (innerErr) {
        // eslint-disable-next-line no-console
        console.error('PDF error extraction failed:', innerErr);
      }
      // eslint-disable-next-line no-console
      console.error('PDF export failed:', e, 'detail:', detail);
      toast.error(detail, { id: `pdf-${r.id}-${which}`, duration: 12000 });
    }
  };

  return (
    <div className="space-y-6" data-testid="rsv-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
            <IdentificationBadge size={22} weight="duotone" className="text-emerald-600" />
            RSV — Registar Stvarnih Vlasnika
          </h3>
          <p className="text-sm text-slate-500 mt-1">Obrazac 1 (Ovlašćenje) i Obrazac 2 (Izjava) — čl. 43 Zakona o sprečavanju pranja novca</p>
        </div>
        <Button onClick={() => { setForm({ ...EMPTY, datum_izdavanja: new Date().toISOString().slice(0, 10) }); setEditingId(null); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rsv-new-btn">
          <Plus size={16} className="mr-2" /> Novo RSV ovlašćenje
        </Button>
      </div>

      <Card className="p-4 bg-white border-slate-200">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraga po klijentu, ovlašćenom licu, PIB-u..." className="pl-9 bg-white border-slate-300" data-testid="rsv-search" />
        </div>
      </Card>

      <Card className="overflow-hidden bg-white border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold tracking-wider uppercase text-slate-600">
                <th className="px-4 py-3">Subjekt</th>
                <th className="px-4 py-3">PIB</th>
                <th className="px-4 py-3">Odgovorno lice</th>
                <th className="px-4 py-3">Ovlašćeno lice</th>
                <th className="px-4 py-3">Tip</th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Ucitavanje...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Nema zapisa</td></tr>}
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rsv-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.client_naziv || r.subj_skraceni_naziv || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{r.subj_pib || '-'}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{r.odg_ime_prezime || '-'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.ovl_ime_prezime || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase tracking-wider">
                      {r.tip_ovlascenja === 'zaposleni' ? 'Zaposleni' : r.tip_ovlascenja === 'zastupnik' ? 'Zastupnik' : 'Punomoćnik'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.datum_izdavanja)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => print1(r)} className="px-2 py-1 hover:bg-emerald-100 rounded text-xs font-semibold text-emerald-700 flex items-center gap-1" title="Štampaj Obrazac 1" data-testid={`rsv-print1-${r.id}`}>
                        <Printer size={13} /> O1
                      </button>
                      <button onClick={() => print2(r)} className="px-2 py-1 hover:bg-teal-100 rounded text-xs font-semibold text-teal-700 flex items-center gap-1" title="Štampaj Obrazac 2" data-testid={`rsv-print2-${r.id}`}>
                        <Printer size={13} /> O2
                      </button>
                      <button onClick={() => exportPdf(r, 1)} className="p-2 hover:bg-indigo-100 rounded" title="Obrazac 1 → PDF" data-testid={`rsv-pdf1-${r.id}`}>
                        <FilePdf size={14} className="text-indigo-600" />
                      </button>
                      <button onClick={() => exportPdf(r, 2)} className="p-2 hover:bg-purple-100 rounded" title="Obrazac 2 → PDF" data-testid={`rsv-pdf2-${r.id}`}>
                        <FilePdf size={14} className="text-purple-600" />
                      </button>
                      <button onClick={() => handleEdit(r)} className="p-2 hover:bg-slate-200 rounded" title="Uredi" data-testid={`rsv-edit-${r.id}`}>
                        <Pencil size={14} className="text-slate-600" />
                      </button>
                      <button onClick={() => handleDelete(r)} disabled={!isAdmin} className={`p-2 rounded ${isAdmin ? 'hover:bg-red-100' : 'opacity-30 cursor-not-allowed'}`} title={isAdmin ? 'Obrisi' : 'Samo administrator moze brisati'} data-testid={`rsv-delete-${r.id}`}>
                        <Trash size={14} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rsv-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Uredi RSV zapis' : 'Novo RSV ovlašćenje'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="pb-3 border-b border-slate-200">
              <Label className="text-xs font-semibold uppercase text-slate-500">Klijent (Subjekt) *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <ClientAutocomplete
                    clients={clients}
                    value={form.client_id}
                    onChange={(id) => pullFromClient(id)}
                    testid="rsv-client-select"
                    placeholder="Pretraga po nazivu ili PIB-u..."
                  />
                </div>
                <Button type="button" variant="outline" onClick={refreshFromClient} disabled={!form.client_id} className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="rsv-refresh-client">
                  Povuci podatke
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Izborom klijenta auto-popunjavaju se naziv, PIB, adresa, telefon i odgovorno lice.</p>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold uppercase text-slate-600">Subjekt (podaci firme)</p>
              <div>
                <Label className="text-xs text-slate-500">Puni naziv</Label>
                <Input value={form.subj_naziv} onChange={(e) => setForm({ ...form, subj_naziv: e.target.value })} className="bg-white border-slate-300" data-testid="rsv-subj-naziv" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Skraćeni naziv</Label>
                  <Input value={form.subj_skraceni_naziv} onChange={(e) => setForm({ ...form, subj_skraceni_naziv: e.target.value })} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">PIB</Label>
                  <Input value={form.subj_pib} onChange={(e) => setForm({ ...form, subj_pib: e.target.value })} className="bg-white border-slate-300" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Adresa</Label>
                  <Input value={form.subj_adresa} onChange={(e) => setForm({ ...form, subj_adresa: e.target.value })} className="bg-white border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Telefon</Label>
                  <Input value={form.subj_telefon} onChange={(e) => setForm({ ...form, subj_telefon: e.target.value })} className="bg-white border-slate-300" />
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-bold uppercase text-slate-600">Odgovorno lice u subjektu (zastupnik firme)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Ime i prezime (PREZIME IME)</Label>
                  <Input value={form.odg_ime_prezime} onChange={(e) => setForm({ ...form, odg_ime_prezime: e.target.value })} className="bg-white border-slate-300" placeholder="UJKASHI BLERIM" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">JMB</Label>
                  <Input value={form.odg_jmb} onChange={(e) => setForm({ ...form, odg_jmb: e.target.value })} className="bg-white border-slate-300" placeholder="13 cifara" />
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-bold uppercase text-slate-600">Ovlašćeno lice (za Registar stvarnih vlasnika)</p>
              <div>
                <Label className="text-xs text-slate-500">Ime i prezime *</Label>
                <FizickoLiceAutocomplete
                  value={form.ovl_ime_prezime}
                  onChange={(v) => setForm(prev => ({ ...prev, ovl_ime_prezime: v }))}
                  onSelect={(fl) => {
                    const ime = (fl.ime || '').toUpperCase();
                    const prezime = (fl.prezime || '').toUpperCase();
                    setForm(prev => ({
                      ...prev,
                      ovl_ime_prezime: `${prezime} ${ime}`.trim(),
                      ovl_jmb: fl.jmb || prev.ovl_jmb,
                      ovl_opstina: (fl.grad || prev.ovl_opstina).toUpperCase ? (fl.grad || '').toUpperCase() : prev.ovl_opstina,
                      ovl_adresa: fl.adresa || prev.ovl_adresa,
                    }));
                  }}
                  placeholder="Unesite 2+ slova: ime, prezime ili JMB..."
                  testid="rsv-ovl-autocomplete"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">JMB *</Label>
                  <Input value={form.ovl_jmb} onChange={(e) => setForm({ ...form, ovl_jmb: e.target.value })} className="bg-white border-slate-300" placeholder="13 cifara" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Opština</Label>
                  <Input value={form.ovl_opstina} onChange={(e) => setForm({ ...form, ovl_opstina: e.target.value })} className="bg-white border-slate-300" placeholder="Ulcinj" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Adresa</Label>
                  <Input value={form.ovl_adresa} onChange={(e) => setForm({ ...form, ovl_adresa: e.target.value })} className="bg-white border-slate-300" placeholder="Kodre bb" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Tip ovlašćenja</Label>
                <div className="flex gap-2">
                  {[
                    { v: 'zaposleni', l: 'Zaposleni' },
                    { v: 'zastupnik', l: 'Zastupnik' },
                    { v: 'punomocnik', l: 'Punomoćnik' },
                  ].map(opt => (
                    <label key={opt.v} className={`flex-1 flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer text-sm ${form.tip_ovlascenja === opt.v ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`rsv-tip-${opt.v}`}>
                      <input type="radio" checked={form.tip_ovlascenja === opt.v} onChange={() => setForm({ ...form, tip_ovlascenja: opt.v })} />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase text-slate-600">Stvarni vlasnici (može ih biti više)</p>
                <Button type="button" size="sm" variant="outline" onClick={addSV} className="text-xs h-7" data-testid="rsv-add-sv">
                  <Plus size={12} className="mr-1" /> Dodaj vlasnika
                </Button>
              </div>
              {(form.stvarni_vlasnici || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">Nema dodanih stvarnih vlasnika — kliknite "Dodaj vlasnika"</p>
              )}
              {(form.stvarni_vlasnici || []).map((sv, i) => (
                <div key={i} className="flex gap-2 items-end" data-testid={`rsv-sv-row-${i}`}>
                  <div className="flex-1">
                    {i === 0 && <Label className="text-xs text-slate-500">Ime i prezime</Label>}
                    <FizickoLiceAutocomplete
                      value={sv.ime_prezime}
                      onChange={(v) => updateSV(i, 'ime_prezime', v)}
                      onSelect={(fl) => {
                        const ime = (fl.ime || '').toUpperCase();
                        const prezime = (fl.prezime || '').toUpperCase();
                        setForm(f => ({
                          ...f,
                          stvarni_vlasnici: f.stvarni_vlasnici.map((x, idx) => idx === i
                            ? { ime_prezime: `${prezime} ${ime}`.trim(), jmb: fl.jmb || x.jmb }
                            : x),
                        }));
                      }}
                      placeholder="PREZIME IME"
                      testid={`rsv-sv-autocomplete-${i}`}
                    />
                  </div>
                  <div className="w-40">
                    {i === 0 && <Label className="text-xs text-slate-500">JMB</Label>}
                    <Input value={sv.jmb} onChange={(e) => updateSV(i, 'jmb', e.target.value)} className="bg-white border-slate-300" placeholder="13 cifara" />
                  </div>
                  <button type="button" onClick={() => removeSV(i)} className="p-2 hover:bg-red-100 rounded mb-0.5" title="Ukloni" data-testid={`rsv-remove-sv-${i}`}>
                    <XIcon size={14} className="text-red-600" />
                  </button>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-200">
              <Label className="text-xs font-semibold uppercase text-slate-500">Datum izdavanja</Label>
              <DateInput value={form.datum_izdavanja} onChange={(e) => setForm({ ...form, datum_izdavanja: e.target.value })} className="border-slate-300" />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Napomena (interno)</Label>
              <Input value={form.napomena} onChange={(e) => setForm({ ...form, napomena: e.target.value })} className="bg-white border-slate-300" placeholder="Opcionalno" />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button variant="outline" onClick={() => setOpen(false)}>Otkaži</Button>
              <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rsv-save">{editingId ? 'Sačuvaj' : 'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
